// packages/web-ui/src/AppShell/ContextPanel-modes/useContextPanelStatus.ts
//
// W2-S3 Task 7.7 — shared status selector for the context-panel modes.
// Per-destination branches read live slice state when a slice exists; modes
// without dedicated state still return `'idle'` so their empty-state copy
// from UX §5.8 renders.
//
// `views`: derives `loading` / `ready` / `error` from the `views` slice
//   (`loading`, `lastError`, `viewsList`). `ViewsMode` triggers the actual
//   `loadViewsList()` call from a mount effect — the status hook MUST stay
//   side-effect-free (Pitfall #5 of W2-S3).

import type { ActiveDestination } from '../../state/navigation.js';
import { useRootStore } from '../../state/store.js';

export type ContextPanelStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Returns the current status of the context-panel body for a given
 * destination. Most destinations still emit `'idle'`; `views` reads its
 * dedicated slice.
 */
export function useContextPanelStatus(destination: ActiveDestination): ContextPanelStatus {
  // Hooks must be called unconditionally — read every slice each render and
  // pick the right field in the destination switch.
  const viewsLoading = useRootStore((s) => s.loading);
  const viewsError = useRootStore((s) => s.lastError);
  const viewsListLength = useRootStore((s) => s.viewsList.length);
  const diffLoading = useRootStore((s) => s.changesetSummariesLoading);
  const diffError = useRootStore((s) => s.changesetSummariesError);
  const diffListLength = useRootStore((s) => s.changesetSummariesList.length);
  const gitLoading = useRootStore((s) => s.gitOverviewLoading);
  const gitError = useRootStore((s) => s.gitOverviewError);
  const gitOverview = useRootStore((s) => s.gitOverview);

  switch (destination) {
    case 'views':
      if (viewsLoading) return 'loading';
      if (viewsError !== null) return 'error';
      if (viewsListLength > 0) return 'ready';
      return 'idle';
    case 'diff':
      if (diffLoading) return 'loading';
      if (diffError !== null) return 'error';
      if (diffListLength > 0) return 'ready';
      return 'idle';
    case 'git':
      if (gitLoading) return 'loading';
      if (gitError !== null) return 'error';
      if (gitOverview !== null) return 'ready';
      return 'idle';
    default:
      return 'idle';
  }
}
